<template>
  <div>

    <div v-if="show" class="ap-overlay" @click.self="!saving && $emit('close')">
      <div class="ap-modal">
        <div class="ap-scrollbar-cap is-top"></div>
        <div class="ap-scrollbar-cap is-bottom"></div>
        <div class="ap-modal-scroll">

          <div class="ap-modal-head">
            <h5 class="ap-modal-title">Select Input Options</h5>
            <button class="ap-modal-close" :disabled="saving" @click="$emit('close')">
              <fa-icon :icon="['fas', 'times']" />
            </button>
          </div>

          <!-- IRB Branch -->
          <div class="ap-opts-section">
            <div class="ap-opts-section-head">
              <fa-icon :icon="['fas', 'landmark']" class="ap-opts-section-icon" />
              <span>IRB Branch</span>
            </div>
            <div class="ap-opts-list">
              <div v-for="(item, i) in localBranches" :key="i" class="ap-opts-item">
                <span class="ap-opts-item-label">{{ item }}</span>
                <button type="button" class="ap-opts-item-remove" :disabled="saving" @click="requestRemove('branch', i)">
                  <fa-icon :icon="['fas', 'times']" />
                </button>
              </div>
              <div v-if="!localBranches.length" class="ap-opts-empty">No options yet.</div>
            </div>
            <div class="ap-opts-add-row">
              <input
                :value="newBranch"
                class="ap-opts-add-input"
                style="text-transform: uppercase"
                placeholder="Add branch…"
                :disabled="saving"
                @input="newBranch = $event.target.value.toUpperCase()"
                @keydown.enter.prevent="addItem('branch')"
              />
              <button type="button" class="ap-btn ap-btn-ghost ap-opts-add-btn" :disabled="saving || !newBranch.trim()" @click="addItem('branch')">
                <fa-icon :icon="['fas', 'plus']" /> Add
              </button>
            </div>
          </div>

          <!-- Auditor -->
          <div class="ap-opts-section">
            <div class="ap-opts-section-head">
              <fa-icon :icon="['fas', 'user-tie']" class="ap-opts-section-icon" />
              <span>Auditor</span>
            </div>
            <div class="ap-opts-list">
              <div v-for="(item, i) in localAuditors" :key="i" class="ap-opts-item">
                <span class="ap-opts-item-label">{{ item }}</span>
                <button type="button" class="ap-opts-item-remove" :disabled="saving" @click="requestRemove('auditor', i)">
                  <fa-icon :icon="['fas', 'times']" />
                </button>
              </div>
              <div v-if="!localAuditors.length" class="ap-opts-empty">No options yet.</div>
            </div>
            <div class="ap-opts-add-row">
              <input
                :value="newAuditor"
                class="ap-opts-add-input"
                style="text-transform: uppercase"
                placeholder="Add auditor…"
                :disabled="saving"
                @input="newAuditor = $event.target.value.toUpperCase()"
                @keydown.enter.prevent="addItem('auditor')"
              />
              <button type="button" class="ap-btn ap-btn-ghost ap-opts-add-btn" :disabled="saving || !newAuditor.trim()" @click="addItem('auditor')">
                <fa-icon :icon="['fas', 'plus']" /> Add
              </button>
            </div>
          </div>

          <!-- Contact Category -->
          <div class="ap-opts-section">
            <div class="ap-opts-section-head">
              <fa-icon :icon="['fas', 'tags']" class="ap-opts-section-icon" />
              <span>Contact Category</span>
            </div>
            <div class="ap-opts-list">
              <div v-for="(item, i) in localCategories" :key="i" class="ap-opts-item">
                <span class="ap-opts-item-label">{{ item }}</span>
                <button type="button" class="ap-opts-item-remove" :disabled="saving" @click="requestRemove('category', i)">
                  <fa-icon :icon="['fas', 'times']" />
                </button>
              </div>
              <div v-if="!localCategories.length" class="ap-opts-empty">No options yet.</div>
            </div>
            <div class="ap-opts-add-row">
              <input
                :value="newCategory"
                class="ap-opts-add-input"
                style="text-transform: uppercase"
                placeholder="Add category…"
                :disabled="saving"
                @input="newCategory = $event.target.value.toUpperCase()"
                @keydown.enter.prevent="addItem('category')"
              />
              <button type="button" class="ap-btn ap-btn-ghost ap-opts-add-btn" :disabled="saving || !newCategory.trim()" @click="addItem('category')">
                <fa-icon :icon="['fas', 'plus']" /> Add
              </button>
            </div>
          </div>

          <!-- Contact Title -->
          <div class="ap-opts-section">
            <div class="ap-opts-section-head">
              <fa-icon :icon="['fas', 'id-badge']" class="ap-opts-section-icon" />
              <span>Contact Title</span>
            </div>
            <div class="ap-opts-list">
              <div v-for="(item, i) in localTitles" :key="i" class="ap-opts-item">
                <span class="ap-opts-item-label">{{ item }}</span>
                <button type="button" class="ap-opts-item-remove" :disabled="saving" @click="requestRemove('title', i)">
                  <fa-icon :icon="['fas', 'times']" />
                </button>
              </div>
              <div v-if="!localTitles.length" class="ap-opts-empty">No options yet.</div>
            </div>
            <div class="ap-opts-add-row">
              <input
                :value="newTitle"
                class="ap-opts-add-input"
                style="text-transform: uppercase"
                placeholder="Add title…"
                :disabled="saving"
                @input="newTitle = $event.target.value.toUpperCase()"
                @keydown.enter.prevent="addItem('title')"
              />
              <button type="button" class="ap-btn ap-btn-ghost ap-opts-add-btn" :disabled="saving || !newTitle.trim()" @click="addItem('title')">
                <fa-icon :icon="['fas', 'plus']" /> Add
              </button>
            </div>
          </div>

        </div>
      </div>

      <!-- Global loading overlay — inside ap-overlay so z-index resolves above the modal box -->
      <transition name="ap-overlay-fade">
        <div v-if="saving" class="ap-page-overlay">
          <div class="ap-page-overlay-box">
            <fa-icon :icon="['fas', 'spinner']" spin class="ap-overlay-spinner" />
            <div class="ap-overlay-title">Saving…</div>
          </div>
        </div>
      </transition>

      <!-- Result notification -->
      <alert-modal :show="showAlert" :message="alertMessage" :status="alertStatus" @close="showAlert = false" />

      <!-- Remove confirmation -->
      <confirm-modal
        :show="!!pendingRemove"
        title="Remove Option"
        confirm-label="Remove"
        :icon="['fas', 'trash']"
        :danger="true"
        @cancel="pendingRemove = null"
        @confirm="confirmRemove"
      >
        Remove <strong>{{ pendingRemove ? pendingRemove.value : '' }}</strong> from {{ pendingRemove ? pendingRemove.label : '' }}?
      </confirm-modal>

    </div>
  </div>
</template>

<script>
import bodyScrollLockMixin from '@/mixins/bodyScrollLockMixin'
import AlertModal from '@/components/shared/AlertModal.vue'
import ConfirmModal from '@/components/shared/ConfirmModal.vue'

const TYPE_CONFIG = {
  branch: { key: 'irb_branches', list: 'localBranches', label: 'IRB Branch' },
  auditor: { key: 'auditors', list: 'localAuditors', label: 'Auditor' },
  category: { key: 'contact_categories', list: 'localCategories', label: 'Contact Category' },
  title: { key: 'contact_titles', list: 'localTitles', label: 'Contact Title' }
}

const INPUT_FIELD = { branch: 'newBranch', auditor: 'newAuditor', category: 'newCategory', title: 'newTitle' }

export default {
  name: 'AdminOptionsModal',
  mixins: [bodyScrollLockMixin],
  components: { AlertModal, ConfirmModal },
  props: {
    show: { type: Boolean, default: false },
    branches: { type: Array, default: () => [] },
    auditors: { type: Array, default: () => [] },
    categories: { type: Array, default: () => [] },
    titles: { type: Array, default: () => [] }
  },
  data () {
    return {
      localBranches: [],
      localAuditors: [],
      localCategories: [],
      localTitles: [],
      newBranch: '',
      newAuditor: '',
      newCategory: '',
      newTitle: '',
      saving: false,
      showAlert: false,
      alertMessage: '',
      alertStatus: 'success',
      pendingRemove: null
    }
  },
  watch: {
    show (val) {
      if (val) {
        this.localBranches = [...this.branches].sort((a, b) => a.localeCompare(b))
        this.localAuditors = [...this.auditors].sort((a, b) => a.localeCompare(b))
        this.localCategories = [...this.categories].sort((a, b) => a.localeCompare(b))
        this.localTitles = [...this.titles].sort((a, b) => a.localeCompare(b))
        this.newBranch = ''
        this.newAuditor = ''
        this.newCategory = ''
        this.newTitle = ''
        this.pendingRemove = null
        this.showAlert = false
      }
    }
  },
  methods: {
    addItem (type) {
      const field = INPUT_FIELD[type]
      const val = this[field].trim().toUpperCase()
      if (!val) return
      const list = this[TYPE_CONFIG[type].list]
      if (list.includes(val)) {
        this.notify(`"${val}" already exists.`, 'error')
        return
      }
      this[field] = ''
      const newArr = [...list, val].sort((a, b) => a.localeCompare(b))
      this.saveOption(type, newArr, () => {
        list.push(val)
        list.sort((a, b) => a.localeCompare(b))
      })
    },
    requestRemove (type, index) {
      const conf = TYPE_CONFIG[type]
      this.pendingRemove = {
        type,
        index,
        value: this[conf.list][index],
        label: conf.label
      }
    },
    confirmRemove () {
      const { type, index } = this.pendingRemove
      this.pendingRemove = null
      const conf = TYPE_CONFIG[type]
      const list = this[conf.list]
      const newArr = list.filter((_, i) => i !== index)
      this.saveOption(type, newArr, () => { list.splice(index, 1) })
    },
    async saveOption (type, newArr, applyFn) {
      this.saving = true
      try {
        const token = localStorage.getItem('adminToken')
        const payload = {
          irb_branches: this.localBranches,
          auditors: this.localAuditors,
          contact_categories: this.localCategories,
          contact_titles: this.localTitles
        }
        payload[TYPE_CONFIG[type].key] = newArr

        const res = await fetch('https://tyng-ca.com.my/api/admin/settings/save.php', {
          method: 'POST',
          headers: { Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' },
          body: JSON.stringify(payload)
        })
        const data = await res.json()
        if (res.ok && data && data.success) {
          applyFn()
          this.$emit('settings-saved', {
            branches: [...this.localBranches],
            auditors: [...this.localAuditors],
            categories: [...this.localCategories],
            titles: [...this.localTitles]
          })
          this.notify('Saved successfully.', 'success')
        } else {
          this.notify(data.message || 'Failed to save.', 'error')
        }
      } catch {
        this.notify('Network error. Please try again.', 'error')
      } finally {
        this.saving = false
      }
    },
    notify (message, status) {
      this.alertMessage = message
      this.alertStatus = status
      this.showAlert = true
    }
  }
}
</script>
